import { useState, useEffect } from "react";
import { toast } from "sonner";
import { DollarSign, Users, Package, TrendingUp, ChevronDown, MapPin, Award, Truck, Printer, PackageCheck, PlayCircle, XCircle, Ban } from "lucide-react";
import api from "../lib/api";
import { money, STATUS_LABELS, STATUS_COLORS, formatApiErrorDetail } from "../lib/helpers";
import { GRADE_OPTIONS, GRADE_LABELS } from "../lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const CANCELLABLE = ["pending_payment", "paid", "received", "grading"];

export default function Admin() {
  const [tab, setTab] = useState("orders");
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [grades, setGrades] = useState({});   // {subId: ["10", "9", ...]}
  const [ship, setShip] = useState({});        // {subId: {weight,length,width,height,tracking,rates,loading}}
  const [busy, setBusy] = useState("");

  const load = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    const [st, sb] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/submissions", { params: filter === "all" ? {} : { status: filter } }),
    ]);
    setStats(st.data); setSubs(sb.data); setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { if (tab === "customers") api.get("/admin/users").then((r) => setUsers(r.data)); }, [tab]);

  const ensureDraft = (s) => {
    setGrades((g) => (g[s.id] ? g : { ...g, [s.id]: s.cards.map((c) => c.grade || "") }));
    setShip((sh) => (sh[s.id] ? sh : { ...sh, [s.id]: { weight: 8, length: 9, width: 6, height: 1, tracking: s.tracking || "", rates: null, loading: false } }));
  };
  const toggleOpen = (s) => { const next = openId === s.id ? null : s.id; setOpenId(next); if (next) ensureDraft(s); };
  const setGrade = (id, i, val) => setGrades((g) => ({ ...g, [id]: g[id].map((x, idx) => (idx === i ? val : x)) }));
  const setShipField = (id, k, v) => setShip((sh) => ({ ...sh, [id]: { ...sh[id], [k]: v } }));

  const updateStatus = async (id, status) => {
    setBusy(`status-${id}`);
    try { await api.patch(`/admin/submissions/${id}`, { status }); toast.success(`Moved to ${STATUS_LABELS[status]}`); await load(false); }
    catch { toast.error("Update failed"); }
    finally { setBusy(""); }
  };

  const publishGrades = async (s) => {
    const g = grades[s.id];
    if (g.some((x) => !x)) { toast.error("Select a grade for every card before publishing."); return; }
    setBusy(`grade-${s.id}`);
    try {
      const { data } = await api.post(`/admin/submissions/${s.id}/grade`, { grades: g.map((grade, index) => ({ index, grade })) });
      toast.success(`Grades published — certs ${data.certnums.join(", ")} issued & customer notified`);
      await load(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not publish grades"); }
    finally { setBusy(""); }
  };

  const getRates = async (s) => {
    const p = ship[s.id];
    setShipField(s.id, "loading", true);
    try {
      const { data } = await api.post(`/admin/submissions/${s.id}/rates`, { weight: Number(p.weight), length: Number(p.length), width: Number(p.width), height: Number(p.height) });
      setShip((sh) => ({ ...sh, [s.id]: { ...sh[s.id], rates: data.rates, insured: data.insured_value, signature: data.signature_required, loading: false } }));
      if (!data.rates.length) toast.error("No rates returned.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not fetch rates"); setShipField(s.id, "loading", false); }
  };

  const buyRate = async (s, rate) => {
    setBusy(`label-${s.id}-${rate.rate_id}`);
    try {
      await api.post(`/admin/submissions/${s.id}/label`, { rate_id: rate.rate_id, carrier: rate.carrier });
      toast.success(`Label purchased — ${rate.carrier} ${rate.service}. Order marked shipped & customer notified.`);
      await load(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not buy label"); }
    finally { setBusy(""); }
  };

  const saveTracking = async (s) => {
    setBusy(`track-${s.id}`);
    try { await api.patch(`/admin/submissions/${s.id}`, { status: s.status, tracking: ship[s.id].tracking }); toast.success("Tracking saved"); await load(false); }
    catch { toast.error("Could not save tracking"); }
    finally { setBusy(""); }
  };

  const statCards = [
    { icon: Package, label: "Submissions", value: stats?.total_submissions ?? "—" },
    { icon: DollarSign, label: "Revenue", value: stats ? money(stats.revenue_cents) : "—" },
    { icon: Users, label: "Customers", value: stats?.total_users ?? "—" },
    { icon: TrendingUp, label: "In Grading", value: stats?.by_status?.grading ?? "—" },
  ];
  const cardLabel = (c) => [c.year && c.year !== "Unknown" ? c.year : "", c.brand, c.subject].filter(Boolean).join(" ") || "Card";

  const btn = "inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-bold transition-colors duration-200";

  const FlowActions = ({ s }) => (
    <div className="flex items-center gap-2 flex-wrap justify-end" onClick={(e) => e.stopPropagation()}>
      {s.status === "pending_payment" && <span className="text-xs font-bold uppercase tracking-widest text-amber-600">Awaiting payment</span>}
      {s.status === "paid" && <button data-testid={`action-received-${s.id}`} disabled={busy === `status-${s.id}`} onClick={() => updateStatus(s.id, "received")} className={`${btn} bg-[#0A0A0A] text-white hover:bg-neutral-800`}><PackageCheck size={15} /> Mark Received</button>}
      {s.status === "received" && <button data-testid={`action-grading-${s.id}`} disabled={busy === `status-${s.id}`} onClick={() => updateStatus(s.id, "grading")} className={`${btn} bg-[#0A0A0A] text-white hover:bg-neutral-800`}><PlayCircle size={15} /> Start Grading</button>}
      {s.status === "graded" && <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">Ready to ship</span>}
      {s.status === "shipped" && <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">Shipped</span>}
      {CANCELLABLE.includes(s.status) && <button data-testid={`action-cancel-${s.id}`} onClick={() => { if (window.confirm("Cancel this order?")) updateStatus(s.id, "cancelled"); }} className={`${btn} border border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-600`}><XCircle size={15} /> Cancel</button>}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-20">
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]">Operations</span>
      <h1 className="font-display font-extrabold text-5xl md:text-6xl tracking-tighter mt-4 mb-10 text-[#0A0A0A]">Fulfillment Console</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        {statCards.map((c, i) => (
          <div key={i} className={`rounded-3xl p-7 ${i === 0 ? "bg-[#0A0A0A] text-white" : "bg-white border border-slate-200"}`}>
            <c.icon className={i === 0 ? "text-white mb-4" : "text-[#0A0A0A] mb-4"} size={24} />
            <div className={`font-display font-extrabold text-4xl ${i === 0 ? "text-white" : "text-[#0A0A0A]"}`}>{c.value}</div>
            <div className={`text-[11px] uppercase tracking-[0.15em] font-semibold mt-1 ${i === 0 ? "text-white/60" : "text-slate-500"}`}>{c.label}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-8 border-b border-slate-200">
        {[["orders", "Orders"], ["customers", "Customers"]].map(([k, l]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
            className={`px-5 py-3 text-sm font-bold border-b-2 -mb-px transition-colors duration-200 ${tab === k ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {tab === "orders" && (
        <>
          <div className="flex items-center gap-3 mb-6">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Filter</span>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-52 bg-white border-slate-200 rounded-full" data-testid="admin-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-[#0A0A0A]">
                <SelectItem value="all">All Submissions</SelectItem>
                {Object.keys(STATUS_LABELS).map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-3xl border border-slate-200 overflow-hidden shadow-sm bg-white">
            {loading ? <p className="p-12 text-center text-slate-400">Loading…</p>
              : subs.length === 0 ? <p className="p-12 text-center text-slate-400">No submissions.</p>
              : (
                <div className="divide-y divide-slate-100">
                  {subs.map((s) => {
                    const cancelled = s.status === "cancelled";
                    const canGrade = s.status === "grading";
                    const isGraded = s.status === "graded" || s.status === "shipped";
                    return (
                      <div key={s.id} data-testid={`admin-sub-${s.id}`}>
                        <div className="p-6 flex flex-wrap items-center justify-between gap-4 hover:bg-slate-50 transition-colors duration-200 cursor-pointer" onClick={() => toggleOpen(s)} data-testid={`admin-open-${s.id}`}>
                          <div className="min-w-0">
                            <div className="flex items-center gap-3 flex-wrap">
                              <ChevronDown size={16} className={`text-slate-400 transition-transform ${openId === s.id ? "rotate-180" : ""}`} />
                              <span className="font-display font-bold text-[#0A0A0A]">{s.tier_name}</span>
                              <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-0.5 rounded-full border ${STATUS_COLORS[s.status] || ""}`}>{STATUS_LABELS[s.status]}</span>
                              {!cancelled && <span className={`text-[10px] font-bold uppercase px-2.5 py-0.5 rounded-full ${s.payment_status === "paid" ? "text-emerald-700 bg-emerald-50" : "text-amber-700 bg-amber-50"}`}>{s.payment_status}</span>}
                            </div>
                            <p className="text-xs text-slate-400 mt-1.5 pl-7">#{s.id.slice(-8)} · {s.user_email} · {s.card_count} card(s) · {money(s.total_amount)}</p>
                          </div>
                          <FlowActions s={s} />
                        </div>

                        {openId === s.id && grades[s.id] && (
                          <div className="bg-[#FAFAFA] border-t border-slate-100 p-6 grid lg:grid-cols-3 gap-6" data-testid={`admin-detail-${s.id}`}>
                            {/* Ship To */}
                            <div className="rounded-2xl bg-white border border-slate-200 p-5">
                              <h4 className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-1.5"><MapPin size={13} /> Ship To</h4>
                              <div className="text-sm text-[#0A0A0A] leading-relaxed">
                                <div className="font-bold">{s.return_name || "—"}</div>
                                <div className="whitespace-pre-line">{s.return_address || "No address on file"}</div>
                                <div className="text-slate-500 mt-2">{s.email || s.user_email}</div>
                              </div>
                              {s.notes && <p className="text-xs text-slate-500 mt-3 border-t border-slate-100 pt-3"><strong>Notes:</strong> {s.notes}</p>}
                              {(s.autograph_auth_count > 0 || s.autograph_grade_count > 0) && (
                                <p className="text-xs text-[#0A0A0A] mt-3 border-t border-slate-100 pt-3">Add-ons: {s.autograph_auth_count || 0} autograph auth · {s.autograph_grade_count || 0} auto grade</p>
                              )}
                            </div>

                            {/* Right column */}
                            <div className="lg:col-span-2 space-y-5">
                              {cancelled ? (
                                <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center text-slate-400">
                                  <Ban className="mx-auto mb-3" size={26} /> This order was cancelled. No further action available.
                                </div>
                              ) : (
                                <>
                                  {/* Grade Cards — only while grading */}
                                  {canGrade && (
                                    <div className="rounded-2xl bg-white border border-slate-200 p-5">
                                      <h4 className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-1.5"><Award size={13} /> Grade Cards</h4>
                                      <div className="space-y-2.5">
                                        {s.cards.map((c, i) => (
                                          <div key={i} className="flex flex-wrap items-center gap-3">
                                            <div className="min-w-0 flex-1 text-sm font-semibold text-[#0A0A0A] truncate">{cardLabel(c)} {c.cardnum && <span className="text-slate-400">#{c.cardnum}</span>}</div>
                                            <Select value={grades[s.id][i]} onValueChange={(v) => setGrade(s.id, i, v)}>
                                              <SelectTrigger className="w-24 bg-white border-slate-200 rounded-lg h-9" data-testid={`grade-${s.id}-${i}-value`}><SelectValue placeholder="Grade" /></SelectTrigger>
                                              <SelectContent className="bg-white border-slate-200 text-[#0A0A0A]">
                                                {GRADE_OPTIONS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                                              </SelectContent>
                                            </Select>
                                            <span className="w-28 text-xs font-bold uppercase tracking-wider text-[#4DB8E8]">{GRADE_LABELS[grades[s.id][i]] || ""}</span>
                                          </div>
                                        ))}
                                      </div>
                                      <button data-testid={`publish-grades-${s.id}`} onClick={() => publishGrades(s)} disabled={busy === `grade-${s.id}`}
                                        className="mt-4 inline-flex items-center gap-2 px-6 py-2.5 bg-[#0A0A0A] text-white text-sm font-bold rounded-full hover:bg-neutral-800 transition-colors disabled:opacity-50">
                                        <Award size={15} /> {busy === `grade-${s.id}` ? "Publishing…" : "Publish grades & issue certs"}
                                      </button>
                                    </div>
                                  )}

                                  {/* Awaiting states info */}
                                  {(s.status === "pending_payment" || s.status === "paid" || s.status === "received") && (
                                    <div className="rounded-2xl bg-white border border-slate-200 p-5">
                                      <h4 className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-3">Cards ({s.card_count})</h4>
                                      <div className="space-y-1.5">
                                        {s.cards.map((c, i) => <div key={i} className="text-sm text-slate-600">{cardLabel(c)} {c.cardnum && <span className="text-slate-400">#{c.cardnum}</span>}</div>)}
                                      </div>
                                      <p className="text-xs text-slate-400 mt-4">
                                        {s.status === "pending_payment" && "Grading unlocks once payment is complete."}
                                        {s.status === "paid" && "Payment received. Mark as Received when the package arrives."}
                                        {s.status === "received" && "Received. Click Start Grading when these cards are in front of your team."}
                                      </p>
                                    </div>
                                  )}

                                  {/* Published grades (read-only) + Shipping — graded/shipped */}
                                  {isGraded && (
                                    <>
                                      <div className="rounded-2xl bg-white border border-slate-200 p-5">
                                        <h4 className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-1.5"><Award size={13} /> Grades Issued</h4>
                                        <div className="space-y-1.5">
                                          {s.cards.map((c, i) => (
                                            <div key={i} className="flex items-center justify-between gap-3 text-sm">
                                              <span className="text-[#0A0A0A] truncate">{cardLabel(c)} {c.cardnum && <span className="text-slate-400">#{c.cardnum}</span>}</span>
                                              <span className="shrink-0"><strong className="font-display text-lg">{c.grade}</strong> <span className="text-[10px] uppercase tracking-wider text-[#4DB8E8] font-bold">{c.label}</span> <span className="text-[10px] text-slate-400 ml-1">CERT {c.certnum}</span></span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>

                                      <div className="rounded-2xl bg-white border border-slate-200 p-5">
                                        <h4 className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-1.5"><Truck size={13} /> Shipping (Shippo)</h4>
                                        <div className="grid grid-cols-4 gap-2 mb-3">
                                          {[["weight", "Weight (oz)"], ["length", "L (in)"], ["width", "W (in)"], ["height", "H (in)"]].map(([k, lbl]) => (
                                            <div key={k}>
                                              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{lbl}</label>
                                              <input data-testid={`ship-${k}-${s.id}`} type="number" min="0" value={ship[s.id][k]} onChange={(e) => setShipField(s.id, k, e.target.value)} className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-2 h-9 text-sm outline-none focus:border-[#0A0A0A]" />
                                            </div>
                                          ))}
                                        </div>
                                        <button data-testid={`get-rates-${s.id}`} onClick={() => getRates(s)} disabled={ship[s.id].loading} className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#0A0A0A] rounded-full text-sm font-bold text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors disabled:opacity-50">
                                          <Truck size={15} /> {ship[s.id].loading ? "Fetching rates…" : "Get shipping rates"}
                                        </button>

                                        {ship[s.id].rates && (
                                          <div className="mt-4 space-y-2" data-testid={`rates-${s.id}`}>
                                            <div className="text-xs font-semibold text-slate-500 mb-1" data-testid={`rate-coverage-${s.id}`}>
                                              Insured for <strong className="text-[#0A0A0A]">${Number(ship[s.id].insured || 0).toFixed(2)}</strong> · Signature: <strong className="text-[#0A0A0A]">{ship[s.id].signature ? "Required" : "Not required"}</strong>
                                            </div>
                                            {ship[s.id].rates.map((r, idx) => (
                                              <div key={r.rate_id} className="flex items-center justify-between gap-3 border border-slate-100 rounded-xl px-4 py-2.5 bg-[#FAFAFA]">
                                                <div className="text-sm">
                                                  <span className="font-bold text-[#0A0A0A]">{r.carrier}</span> <span className="text-slate-500">{r.service}</span>
                                                  {r.days ? <span className="text-xs text-slate-400"> · {r.days} day(s)</span> : null}
                                                </div>
                                                <div className="flex items-center gap-3 shrink-0">
                                                  <span className="font-bold text-[#0A0A0A]">${r.amount}</span>
                                                  <button data-testid={`buy-rate-${s.id}-${idx}`} onClick={() => buyRate(s, r)} disabled={busy === `label-${s.id}-${r.rate_id}`} className="inline-flex items-center gap-1.5 px-4 h-9 bg-[#0A0A0A] text-white rounded-lg text-sm font-bold hover:bg-neutral-800 transition-colors disabled:opacity-50">
                                                    {busy === `label-${s.id}-${r.rate_id}` ? "Buying…" : "Buy 4×6"}
                                                  </button>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}

                                        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-slate-100">
                                          <input data-testid={`tracking-${s.id}`} value={ship[s.id].tracking} onChange={(e) => setShipField(s.id, "tracking", e.target.value)} placeholder="Or enter tracking manually" className="flex-1 min-w-[180px] bg-white border border-slate-200 rounded-lg px-3 h-9 text-sm outline-none focus:border-[#0A0A0A]" />
                                          <button data-testid={`save-tracking-${s.id}`} onClick={() => saveTracking(s)} disabled={busy === `track-${s.id}`} className="px-4 h-9 border border-slate-200 rounded-lg text-sm font-bold hover:border-[#0A0A0A] transition-colors">Save</button>
                                          {s.label_url && <a data-testid={`label-link-${s.id}`} href={s.label_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 h-9 bg-[#0A0A0A] text-white rounded-lg text-sm font-bold hover:bg-neutral-800 transition-colors"><Printer size={14} /> Print label</a>}
                                        </div>
                                        {s.tracking && <p className="text-xs text-emerald-600 font-semibold mt-3">Tracking: {s.tracking} {s.carrier && `(${s.carrier})`}</p>}
                                      </div>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
          </div>
        </>
      )}

      {tab === "customers" && (
        <div className="rounded-3xl border border-slate-200 overflow-hidden shadow-sm bg-white">
          {users.length === 0 ? <p className="p-12 text-center text-slate-400">No customers yet.</p> : (
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] text-[11px] uppercase tracking-widest text-slate-400">
                <tr>
                  <th className="text-left font-bold px-6 py-4">Name</th>
                  <th className="text-left font-bold px-6 py-4">Email</th>
                  <th className="text-left font-bold px-6 py-4">Role</th>
                  <th className="text-right font-bold px-6 py-4">Orders</th>
                  <th className="text-right font-bold px-6 py-4">Spent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 font-semibold text-[#0A0A0A]">{u.name || "—"}</td>
                    <td className="px-6 py-4 text-slate-600">{u.email}</td>
                    <td className="px-6 py-4"><span className={`text-[10px] font-bold uppercase px-2.5 py-0.5 rounded-full ${u.role === "admin" ? "bg-[#0A0A0A] text-white" : "bg-slate-100 text-slate-600"}`}>{u.role}</span></td>
                    <td className="px-6 py-4 text-right font-semibold">{u.submissions}</td>
                    <td className="px-6 py-4 text-right font-semibold">{money(u.spent_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
